import React, { useState, useEffect } from 'react';
import indiaData from './states-and-districts.json';
import config from './config';

const steps = [
  "Artisan / Beneficiary Details",
  "Product Details",
  "Participation History",
  "Mandatory Documents",
  "Declaration"
];

const TulipNominationForm = () => {
  const [activeTab, setActiveTab] = useState('form');

  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false); 
  const [applicationId, setApplicationId] = useState(''); 
  
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpValue, setOtpValue] = useState('');
  const [otpLoading, setOtpLoading] = useState(false);

  const [trackAppId, setTrackAppId] = useState('');
  const [trackMobile, setTrackMobile] = useState('');
  const [trackingData, setTrackingData] = useState(null);
  const [isTracking, setIsTracking] = useState(false);
  const [trackError, setTrackError] = useState('');

  const [formData, setFormData] = useState({
    hasArtisanCard: '', artisanId: '', name: '', fatherHusbandName: '', age: '', gender: '', aadhaarNumber: '', 
    category: 'SC', mobile: '', address: '', district: '', state: '',
    pinCode: '', familyIncome: '', nsfdcLoanAvailed: '', loanSanctionAmount: '', loanSanctionDate: '',
    belongsToCluster: '', clusterName: '', clusterArtisanCount: '',
    productDetails: '', productDescription: '', productPhotos: [], 
    isFirstTime: '', previousParticipationsCount: '', 
    melaName1: '', melaDate1: '', melaName2: '', melaDate2: '', melaName3: '', melaDate3: '',
    artisanCard: null, scCertificate: null, incomeCertificate: null, aadhaarVoterId: null, proofOfLoan: null,
    agreedToConduct: false,
  });

  const availableDistricts = indiaData.states.find(s => s.state === formData.state)?.districts || [];

  useEffect(() => {
    return () => {
      if (formData.productPhotos && formData.productPhotos.length > 0) {
        formData.productPhotos.forEach(file => {
           if(file.preview) URL.revokeObjectURL(file.preview);
        });
      }
    };
  }, [formData.productPhotos]);

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      setFormData(prev => ({ ...prev, [name]: files }));
    } else if (name === 'state') {
      setFormData(prev => ({ ...prev, state: value, district: '' }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
    
    if (name === 'mobile') {
      setOtpSent(false);
      setOtpVerified(false);
      setOtpValue('');
    }
  };

  const handleSendOtp = async () => {
    if (!formData.mobile || formData.mobile.length !== 10) {
      alert("Please enter a valid 10-digit mobile number.");
      return;
    }
    setOtpLoading(true);
    try {
      const response = await fetch(`${config.API_BASE_URL}/Otp/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobileNumber: formData.mobile }) 
      });
      const rawText = await response.text();
      if (response.ok) {
        setOtpSent(true);
        alert("OTP sent successfully!");
      } else {
        try {
          const jsonResult = JSON.parse(rawText);
          alert(jsonResult.message || `Server Error ${response.status}.`);
        } catch (e) {
          alert(`Server crashed with status ${response.status}.`);
        }
      }
    } catch (error) {
      alert("Backend server is not reachable.");
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setOtpLoading(true);
    try {
       const response = await fetch(`${config.API_BASE_URL}/Otp/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ mobileNumber: formData.mobile, otp: otpValue })
      });
      const result = await response.json();
      if (response.ok) {
        setOtpVerified(true);
        alert("Mobile number verified successfully!");
      } else {
        alert(result.message || "Invalid OTP.");
      }
    } catch (error) {
      alert("Network error during verification.");
    } finally {
      setOtpLoading(false);
    }
  };

  const nextStep = () => {
    if (currentStep === 0 && !otpVerified) {
      alert("Please verify your mobile number with OTP before continuing.");
      return; 
    }
    if (currentStep === 1 && (!formData.productPhotos || formData.productPhotos.length !== 3)) {
      alert("You must select EXACTLY 3 product photos to continue.");
      return; 
    }
    if (currentStep === 3) {
      if (!formData.hasArtisanCard) {
        alert("Please specify whether you have an Artisan Card.");
        return;
      }
      if (formData.hasArtisanCard === 'Yes') {
        if (!formData.artisanId || formData.artisanId.trim() === '') {
          alert("Please enter your Artisan ID.");
          return;
        }
        if (!formData.artisanCard || formData.artisanCard.length === 0) {
          alert("Please upload your Artisan Card.");
          return;
        }
      }

      const mandatory = ['scCertificate', 'incomeCertificate', 'aadhaarVoterId'];
      const missing = mandatory.filter(key => !formData[key] || formData[key].length === 0);
      
      if (missing.length > 0) {
        alert("Please upload the mandatory documents (SC, Income, and Aadhaar) before continuing.");
        return;
      }
      if (formData.nsfdcLoanAvailed === 'Yes' && (!formData.proofOfLoan || formData.proofOfLoan.length === 0)) {
        alert("Please upload the Proof of Loan/Assistance document.");
        return;
      }
    }
    if (currentStep < steps.length - 1) setCurrentStep((prev) => prev + 1);
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep((prev) => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.agreedToConduct) {
        alert("Please agree to the Code of Conduct before submitting.");
        return;
    }
    setIsSubmitting(true);
    const submitData = new FormData();

    Object.keys(formData).forEach(key => {
      const fileKeys = ['productPhotos', 'artisanCard', 'scCertificate', 'incomeCertificate', 'aadhaarVoterId', 'proofOfLoan'];
      
      if (key === 'clusterName' || key === 'clusterArtisanCount') {
          if (formData.belongsToCluster === 'Yes' && formData[key] !== '') {
             submitData.append(key, formData[key]);
          }
      } else if (key === 'artisanId') {
          if (formData.hasArtisanCard === 'Yes' && formData[key] !== '') {
             submitData.append(key, formData[key]);
          }
      } else if (key === 'loanSanctionAmount' || key === 'loanSanctionDate') {
          if (formData.nsfdcLoanAvailed === 'Yes' && formData[key] !== '') {
             submitData.append(key, formData[key]);
          }
      } else if (!fileKeys.includes(key) && formData[key] !== '' && formData[key] !== null) {
          if (key.startsWith('melaDate')) {
              submitData.append(key, `${formData[key]}-01`); 
          } else {
              submitData.append(key, formData[key]);
          }
        }
    });

    const appendSafe = (key, fileList) => {
        if (fileList && fileList.length > 0) {
            submitData.append(key, fileList[0]);
        }
    };

    if (formData.hasArtisanCard === 'Yes') {
      appendSafe('artisanCard', formData.artisanCard);
    }
    appendSafe('scCertificate', formData.scCertificate);
    appendSafe('incomeCertificate', formData.incomeCertificate);
    appendSafe('aadhaarVoterId', formData.aadhaarVoterId);
    
    if (formData.nsfdcLoanAvailed === 'Yes') {
        appendSafe('proofOfLoan', formData.proofOfLoan);
    }

    if (formData.productPhotos && formData.productPhotos.length > 0) {
      for (let i = 0; i < formData.productPhotos.length; i++) {
        submitData.append('productPhotos', formData.productPhotos[i]);
      }
    }

    try {
        const response = await fetch(`${config.API_BASE_URL}/Exhibition/nomination-upload`, {
        method: 'POST',
        body: submitData,
      });

      if (response.ok) {
        const result = await response.json();
        setApplicationId(result.applicationId || 'TULIP-APP-PENDING');
        setIsSubmitted(true);
      } else {
        const errorText = await response.text();
        alert("Submission failed: " + errorText);
      }
    } catch (error) {
      alert("Failed to connect to the server. Is the API running?");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTrackSubmit = async (e) => {
    e.preventDefault();
    setIsTracking(true);
    setTrackError('');
    setTrackingData(null);

    try {
      const response = await fetch(
        `${config.API_BASE_URL}/tracking/status?applicationId=${trackAppId}&mobileNumber=${trackMobile}`
      );

      if (response.ok) {
        const data = await response.json();
        setTrackingData(Array.isArray(data) ? data : [data]);
      } else if (response.status === 404) {
        setTrackError("No application found with these details.");
      } else {
        setTrackError("Something went wrong while fetching data.");
      }
    } catch (err) {
      setTrackError("Network Error: Could not connect to tracking server.");
    } finally {
      setIsTracking(false);
    }
  };

  const inputClass = "mt-1 w-full p-2.5 bg-gray-50 border border-gray-300 rounded-md text-sm shadow-sm placeholder-gray-400 focus:outline-none focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  // =======================================================================
  // 1. ADDED LOADING OVERLAY COMPONENT DEFINITION
  // =======================================================================
  const LoadingOverlay = () => (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-white/80 backdrop-blur-sm">
      <div className="bg-white p-8 rounded-2xl shadow-2xl border border-gray-100 text-center max-w-sm w-full mx-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-blue-600 mx-auto mb-4"></div>
        <h3 className="text-lg font-bold text-gray-900">Processing Registrations</h3>
        <p className="text-sm text-gray-500 mt-2 leading-relaxed">
          We are securely uploading your documents. This may take a few moments depending on your file sizes. Please do not refresh the page.
        </p>
      </div>
    </div>
  );

  // =======================================================================
  // EXACT ALIGNED SUBMISSION SUCCESS SCREEN
  // =======================================================================
  if (isSubmitted && activeTab === 'form') {
    return (
      <div className="min-h-screen bg-[#f4f6f8] flex flex-col font-sans">
        <div className="bg-[#1a1b41] text-white text-xs px-4 py-2 flex justify-between items-center border-b-[3px] border-[#ff9933]">
          <div className="font-semibold tracking-wide">भारत सरकार | GOVERNMENT OF INDIA</div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block font-medium">Ministry of Social Justice & Empowerment</div>
            <a href="https://dms.nsfdcdigital.in" target="_blank" rel="noopener noreferrer" className="bg-[#ff9933] hover:bg-orange-400 text-[#1a1b41] px-3 py-1 rounded font-bold transition-colors">Admin Login</a>
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-[500px] w-full bg-white p-10 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 text-center">
            
            <div className="w-20 h-20 bg-[#f0fdf4] rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-[#22c55e]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            
            <h2 className="text-3xl font-bold text-[#111827] mb-3">Application Submitted</h2>
            <p className="text-[#4b5563] mb-8 leading-relaxed">
              Your nomination for the Bharat Tulip program has been successfully recorded.
            </p>
            
            <div className="bg-[#f9fafb] py-6 px-4 w-full rounded-xl border border-gray-200 mb-8 shadow-sm text-center">
              <p className="text-[11px] text-[#6b7280] uppercase tracking-[0.2em] font-bold mb-2">Registration ID</p>
              <p className="text-xl sm:text-2xl md:text-[28px] font-black text-[#2f5df2] tracking-tight m-0 leading-none break-all sm:break-normal">
                {applicationId}
              </p>
            </div>
            
            <button 
              onClick={() => { window.location.reload(); }} 
              className="w-full py-3.5 bg-[#3b6df6] text-white font-semibold rounded-lg shadow-sm hover:bg-[#2563eb] transition-all active:scale-[0.98]"
            >
              Submit Another Application
            </button>

          </div>
        </div>
      </div>
    );
  }

  // =======================================================================
  // MAIN FORM RENDER
  // =======================================================================
  return (
    <div className="min-h-screen bg-[#f4f6f8] font-sans flex flex-col">
      {/* 3. PLACED THE OVERLAY AT THE VERY TOP OF RETURNED JSX */}
      {isSubmitting && <LoadingOverlay />}

      <div className="bg-white shadow-sm z-10 relative">
        <div className="bg-[#1a1b41] text-white text-xs px-4 py-2 flex justify-between items-center border-b-[3px] border-[#ff9933]">
          <div className="font-semibold tracking-wide">भारत सरकार | GOVERNMENT OF INDIA</div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block font-medium">Ministry of Social Justice & Empowerment</div>
            <a href="https://dms.nsfdcdigital.in" target="_blank" rel="noopener noreferrer" className="bg-[#ff9933] hover:bg-orange-400 text-[#1a1b41] px-3 py-1 rounded font-bold transition-colors shadow-sm">Admin Login</a>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between py-4 gap-4">
            <div className="flex items-center space-x-4 w-full md:w-1/3">
              <img src="https://ik.imagekit.io/nanoplex/Project%20Image/NSFDC/white_border_circle_1OJdwhvTJ5.png?updatedAt=1769507158042" alt="NSFDC" className="h-20 w-20 object-contain bg-[#fdfdfd] rounded-full p-1 shadow-sm" />
              <div className="border-l-2 border-gray-200 pl-4">
                <h2 className="text-sm font-bold text-[#1a1b41] uppercase tracking-wide">National Scheduled Castes Finance and Development Corporation</h2>
              </div>
            </div>

            <div className="text-center w-full md:w-1/3 flex flex-col items-center">
               <h1 className="text-2xl md:text-3xl font-black text-[#1a1b41] uppercase tracking-widest">Bharat Tulip</h1>
               <div className="h-0.5 w-16 bg-[#ff9933] my-1 rounded-full"></div>
               <p className="text-xs font-bold text-gray-600 uppercase tracking-wider">Artisan Registration Portal</p>
            </div>

            <div className="flex justify-center md:justify-end w-full md:w-1/3">
              <img src="https://ik.imagekit.io/nanoplex/Project%20Image/NSFDC/tuliplogo_round_KfVHZMoaX.png" alt="TULIP" className="h-20 w-20 object-contain mix-blend-multiply" />
            </div>
          </div>
        </div>

        <div className="flex justify-center border-b border-gray-200 bg-white">
          <div className="flex">
            <button className={`px-8 py-3 font-bold text-sm tracking-wide transition-colors ${activeTab === 'form' ? 'text-[#ff9933] border-b-4 border-[#ff9933]' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`} onClick={() => setActiveTab('form')}>Application Form</button>
            <div className="w-px bg-gray-300 my-3"></div>
            <button className={`px-8 py-3 font-bold text-sm tracking-wide transition-colors ${activeTab === 'track' ? 'text-[#ff9933] border-b-4 border-[#ff9933]' : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'}`} onClick={() => setActiveTab('track')}>Track Application</button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8 flex-1">
        {activeTab === 'form' && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden flex flex-col md:flex-row min-h-[650px] animate-fadeIn">
            
            <div className="md:w-72 bg-[#f8fafc] border-r border-gray-200 p-8 flex-shrink-0">
              <h3 className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-8 hidden md:block">Application Steps</h3>
              
              <div className="flex md:hidden items-center justify-between mb-4">
                <span className="text-sm font-semibold text-gray-900">Step {currentStep + 1} of {steps.length}</span>
                <span className="text-sm font-medium text-blue-600">{steps[currentStep]}</span>
              </div>
              <div className="w-full bg-gray-200 h-2 rounded-full md:hidden mb-2">
                <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}></div>
              </div>

              <ul className="hidden md:flex flex-col space-y-8 relative before:absolute before:inset-0 before:ml-4 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-transparent before:via-gray-200 before:to-transparent">
                {steps.map((step, index) => (
                  <li key={index} className="relative flex items-center gap-4 z-10">
                    <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 text-sm font-bold bg-white transition-all duration-300 shadow-sm
                      ${currentStep === index ? 'border-blue-600 text-blue-600 ring-4 ring-blue-50' : 
                        currentStep > index ? 'border-green-500 bg-green-500 text-white' : 'border-gray-300 text-gray-400'}`}
                    >
                      {currentStep > index ? <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg> : index + 1}
                    </div>
                    <span className={`text-sm ${currentStep === index ? 'font-bold text-gray-900' : currentStep > index ? 'font-medium text-gray-600' : 'font-medium text-gray-400'}`}>
                      {step}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex-1 flex flex-col bg-white">
              <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
                <div className="p-6 sm:p-10 flex-1 overflow-y-auto">
                  <div className="mb-6 border-b border-gray-100 pb-4">
                    <h2 className="text-2xl font-bold text-gray-800">{steps[currentStep]}</h2>
                    <p className="text-sm text-gray-500 mt-1">Please provide accurate information for verification.</p>
                  </div>
                  
                  {currentStep === 0 && (
                    <div className="mb-6 p-4 bg-yellow-50 border-l-4 border-yellow-400 text-yellow-800 text-sm rounded-r-md flex items-start gap-2 shadow-sm">
                      <svg className="w-5 h-5 text-yellow-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                      <div>
                        <span className="font-bold"></span> Only artisans / beneficiaries involved in the production of sellable products are eligible to apply.
                      </div>
                    </div>
                  )}
                  
                  {/* STEP 1: Application Details */}
                  {currentStep === 0 && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 animate-fadeIn">
                      <div>
                        <label className={labelClass}>Full Name <span className="text-red-500">*</span></label>
                        <input type="text" name="name" required value={formData.name} onChange={handleChange} className={inputClass} placeholder="Enter full name" />
                      </div>
                      <div>
                        <label className={labelClass}>Father’s/Husband’s Name <span className="text-red-500">*</span></label>
                        <input type="text" name="fatherHusbandName" required value={formData.fatherHusbandName} onChange={handleChange} className={inputClass} />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className={labelClass}>Age <span className="text-red-500">*</span></label>
                          <input type="number" name="age" required value={formData.age} onChange={handleChange} className={inputClass} />
                        </div>
                        <div>
                          <label className={labelClass}>Gender <span className="text-red-500">*</span></label>
                          <select name="gender" required value={formData.gender} onChange={handleChange} className={inputClass}>
                            <option value="">Select</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Transgender">Transgender</option>
                          </select>
                        </div>
                      </div>
                      
                      <div className="sm:col-span-2 p-5 bg-gray-50 border border-gray-200 rounded-xl shadow-sm">
                        <label className={labelClass}>Mobile Number <span className="text-red-500">*</span></label>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <input 
                            type="tel" name="mobile" required maxLength="10" disabled={otpVerified}
                            value={formData.mobile} onChange={handleChange} 
                            className={`${inputClass} mt-0 sm:w-1/2 ${otpVerified ? 'bg-green-50 border-green-300' : ''}`} 
                            placeholder="10-digit mobile number" 
                          />
                          {!otpVerified && !otpSent && (
                            <button type="button" onClick={handleSendOtp} disabled={otpLoading || formData.mobile.length !== 10} className="px-6 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-lg shadow-sm hover:bg-blue-700 disabled:bg-gray-400 transition-colors">
                              {otpLoading ? 'Sending...' : 'Get OTP'}
                            </button>
                          )}
                        </div>

                        {otpSent && !otpVerified && (
                          <div className="mt-4 flex flex-col sm:flex-row gap-3 animate-fadeIn">
                            <input type="text" placeholder="Enter 6-digit OTP" value={otpValue} onChange={(e) => setOtpValue(e.target.value)} maxLength="6" className="p-2.5 border border-gray-300 rounded-lg text-sm sm:w-1/3 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"/>
                            <button type="button" onClick={handleVerifyOtp} disabled={otpLoading || otpValue.length < 4} className="px-6 py-2.5 bg-green-600 text-white text-sm font-bold rounded-lg shadow-sm hover:bg-green-700 disabled:bg-gray-400 transition-colors">
                              {otpLoading ? 'Verifying...' : 'Verify'}
                            </button>
                          </div>
                        )}

                        {otpVerified && (
                          <div className="mt-3 text-sm font-bold text-green-700 flex items-center gap-1.5 bg-green-100 w-max px-3 py-1 rounded-full">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7"></path></svg>
                            Mobile Number Verified
                          </div>
                        )}
                      </div>
                      
                      <div>
                        <label className={labelClass}>Aadhaar Number <span className="text-red-500">*</span></label>
                        <input type="text" name="aadhaarNumber" required maxLength="12" pattern="\d{12}" title="Please enter a valid 12-digit Aadhaar number" value={formData.aadhaarNumber} onChange={handleChange} className={inputClass} placeholder="12-digit Aadhaar Number" />
                      </div>

                      <div>
                        <label className={labelClass}>Category <span className="text-red-500">*</span></label>
                        <input type="text" value="Scheduled Caste (SC)" disabled className={`${inputClass} bg-gray-100 text-gray-500 font-medium cursor-not-allowed`} />
                      </div>

                      <div className="sm:col-span-2">
                        <label className={labelClass}>Complete Address <span className="text-red-500">*</span></label>
                        <textarea name="address" required value={formData.address} onChange={handleChange} rows="2" className={inputClass}></textarea>
                      </div>

                      <div>
                        <label className={labelClass}>State <span className="text-red-500">*</span></label>
                        <select name="state" required value={formData.state} onChange={handleChange} className={inputClass}>
                          <option value="">Select State</option>
                          {indiaData.states.map((s, index) => (
                            <option key={index} value={s.state}>{s.state}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className={labelClass}>District <span className="text-red-500">*</span></label>
                        <select name="district" required value={formData.district} onChange={handleChange} className={inputClass} disabled={!formData.state}>
                          <option value="">Select District</option>
                          {availableDistricts.map((district, index) => (
                            <option key={index} value={district}>{district}</option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className={labelClass}>Pin Code <span className="text-red-500">*</span></label>
                        <input type="text" name="pinCode" required value={formData.pinCode} onChange={handleChange} className={inputClass} />
                      </div>
                      <div>
                        <label className={labelClass}>Annual Family Income (₹) <span className="text-red-500">*</span></label>
                        <input type="number" name="familyIncome" required value={formData.familyIncome} onChange={handleChange} className={inputClass} />
                      </div>
                      
                      <div className="sm:col-span-2 p-5 bg-[#f8fafc] rounded-xl border border-blue-100 mt-2 shadow-sm">
                        <label className="block text-sm font-semibold text-gray-800 mb-3">NSFDC Loan Availed? <span className="text-red-500">*</span></label>
                        <div className="flex space-x-6">
                          <label className="flex items-center space-x-2 cursor-pointer group">
                            <input type="radio" name="nsfdcLoanAvailed" value="Yes" onChange={handleChange} checked={formData.nsfdcLoanAvailed === 'Yes'} required className="text-blue-600 focus:ring-blue-500 w-4 h-4" />
                            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">Yes</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer group">
                            <input type="radio" name="nsfdcLoanAvailed" value="No" onChange={handleChange} checked={formData.nsfdcLoanAvailed === 'No'} required className="text-blue-600 focus:ring-blue-500 w-4 h-4" />
                            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">No</span>
                          </label>
                        </div>
                        {formData.nsfdcLoanAvailed === 'Yes' && (
                          <div className="mt-4 pt-4 border-t border-blue-100 animate-fadeIn grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className={labelClass}>Loan Sanction Amount (₹) <span className="text-red-500">*</span></label>
                              <input type="number" name="loanSanctionAmount" required value={formData.loanSanctionAmount} onChange={handleChange} className={inputClass} placeholder="Enter Amount" />
                            </div>
                            <div>
                              <label className={labelClass}>Loan Sanction Date <span className="text-red-500">*</span></label>
                              <input type="date" name="loanSanctionDate" required value={formData.loanSanctionDate} onChange={handleChange} className={inputClass} />
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="sm:col-span-2 p-5 bg-[#f8fafc] rounded-xl border border-blue-100 mt-2 shadow-sm">
                        <label className="block text-sm font-semibold text-gray-800 mb-3">Belongs to any Cluster/NGO/SHG etc? <span className="text-red-500">*</span></label>
                        <div className="flex space-x-6">
                          <label className="flex items-center space-x-2 cursor-pointer group">
                            <input type="radio" name="belongsToCluster" value="Yes" onChange={handleChange} checked={formData.belongsToCluster === 'Yes'} required className="text-blue-600 focus:ring-blue-500 w-4 h-4" />
                            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">Yes</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer group">
                            <input type="radio" name="belongsToCluster" value="No" onChange={handleChange} checked={formData.belongsToCluster === 'No'} required className="text-blue-600 focus:ring-blue-500 w-4 h-4" />
                            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">No</span>
                          </label>
                        </div>
                        {formData.belongsToCluster === 'Yes' && (
                          <div className="mt-4 pt-4 border-t border-blue-100 animate-fadeIn grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                              <label className={labelClass}>Name of Cluster/NGO/SHG etc <span className="text-red-500">*</span></label>
                              <input type="text" name="clusterName" required value={formData.clusterName} onChange={handleChange} className={inputClass} placeholder="Enter Name" />
                            </div>
                            <div>
                              <label className={labelClass}>Total Number of Artisans <span className="text-red-500">*</span></label>
                              <input type="number" name="clusterArtisanCount" required min="1" value={formData.clusterArtisanCount} onChange={handleChange} className={inputClass} placeholder="e.g. 15" />
                            </div>
                          </div>
                        )}
                      </div>

                    </div>
                  )}

                  {/* STEP 2: Product Details */}
                  {currentStep === 1 && (
                    <div className="space-y-6 animate-fadeIn">
                      <div>
                        <label className={labelClass}>Product Category <span className="text-red-500">*</span></label>
                        <select name="productDetails" required value={formData.productDetails} onChange={handleChange} className={`${inputClass} sm:w-1/2`}>
                          <option value="">Select a category</option>
                          <option value="Handicrafts">Handicrafts</option>
                          <option value="Handlooms">Handlooms</option>
                          <option value="Other Sellable Products">Other Sellable Products</option>
                        </select>
                      </div>
                      <div>
                        <label className={labelClass}>Product Description <span className="text-red-500">*</span></label>
                        <textarea name="productDescription" required value={formData.productDescription} onChange={handleChange} rows="4" className={inputClass} placeholder="Describe the materials, technique, and cultural significance..."></textarea>
                      </div>
                      
                      <div>
                        <label className={labelClass}>Product Photos (Exactly 3 Required) <span className="text-red-500">*</span></label>
                        <p className="text-xs text-gray-500 font-medium mb-3">Accepted formats: JPG, PNG, WEBP (Max 5MB each)</p>
                        {formData.productPhotos && formData.productPhotos.length > 0 && (
                          <div className="flex flex-wrap gap-4 mb-4 mt-2">
                            {formData.productPhotos.map((file, idx) => (
                              <div key={idx} className="relative w-28 h-28 rounded-xl overflow-hidden border-2 border-gray-200 shadow-sm group">
                                <img src={file.preview} alt={`Preview ${idx + 1}`} className="w-full h-full object-cover" />
                                <button type="button" onClick={() => setFormData(prev => ({...prev, productPhotos: prev.productPhotos.filter((_, i) => i !== idx)}))} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1.5 shadow-md hover:bg-red-700 transition-colors">
                                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12"></path></svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        {(!formData.productPhotos || formData.productPhotos.length < 3) && (
                          <div className="mt-2 flex flex-col items-center justify-center px-6 pt-6 pb-7 border-2 border-gray-300 border-dashed rounded-xl hover:border-blue-400 hover:bg-blue-50/50 transition-colors bg-gray-50">
                            <div className="space-y-2 text-center">
                              <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48"><path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                              <div className="flex text-sm text-gray-600 justify-center">
                                <label className="relative cursor-pointer bg-white border border-gray-300 px-4 py-2 shadow-sm rounded-md font-semibold text-blue-600 hover:bg-gray-50 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500 transition-all">
                                  <span>Click to Upload Images</span>
                                  <input type="file" name="productPhotos" multiple accept="image/jpeg, image/png, image/webp" onChange={(e) => { const newFiles = Array.from(e.target.files).map(f => Object.assign(f, {preview: URL.createObjectURL(f)})); setFormData(prev => ({ ...prev, productPhotos: [...(prev.productPhotos || []), ...newFiles].slice(0, 3) })); e.target.value = null; }} className="sr-only" />
                                </label>
                              </div>
                              <p className="text-xs text-gray-500 font-medium">You need {3 - (formData.productPhotos?.length || 0)} more image(s).</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* STEP 3: Participation History */}
                  {currentStep === 2 && (
                    <div className="space-y-6 animate-fadeIn">
                      <div className="p-6 border border-gray-200 rounded-xl bg-white shadow-sm">
                        <label className="block text-base font-semibold text-gray-800 mb-4">Is this your first time participating? <span className="text-red-500">*</span></label>
                        <div className="flex flex-col sm:flex-row sm:space-x-8 space-y-3 sm:space-y-0">
                          <label className="flex items-center space-x-3 cursor-pointer group">
                            <input type="radio" name="isFirstTime" value="Yes" onChange={handleChange} checked={formData.isFirstTime === 'Yes'} required className="text-blue-600 focus:ring-blue-500 w-5 h-5 border-gray-300" />
                            <span className="text-gray-700 font-medium group-hover:text-blue-600 transition-colors">Yes, it's my first time</span>
                          </label>
                          <label className="flex items-center space-x-3 cursor-pointer group">
                            <input type="radio" name="isFirstTime" value="No" onChange={handleChange} checked={formData.isFirstTime === 'No'} required className="text-blue-600 focus:ring-blue-500 w-5 h-5 border-gray-300" />
                            <span className="text-gray-700 font-medium group-hover:text-blue-600 transition-colors">No, I've participated before</span>
                          </label>
                        </div>
                      </div>

                      {formData.isFirstTime === 'No' && (
                        <div className="p-6 border border-gray-200 rounded-xl bg-gray-50 shadow-inner animate-fadeIn mt-4">
                          <div className="mb-6">
                            <label className={labelClass}>Total Number of Previous Participations <span className="text-red-500">*</span></label>
                            <input type="number" min="1" name="previousParticipationsCount" required value={formData.previousParticipationsCount} onChange={handleChange} className={`${inputClass} sm:w-1/2`} placeholder="e.g. 5" />
                          </div>
                          {parseInt(formData.previousParticipationsCount) > 0 && (
                            <div className="space-y-4">
                              <label className="block text-sm font-bold text-gray-800 mb-2">Details of Last Participations (Max 3 records)</label>
                              {Array.from({ length: Math.min(parseInt(formData.previousParticipationsCount), 3) }).map((_, index) => {
                                const num = index + 1;
                                return (
                                  <div key={num} className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-5 border border-blue-100 rounded-lg bg-white shadow-sm">
                                    <h4 className="sm:col-span-2 text-xs font-bold text-blue-600 uppercase tracking-wider border-b border-gray-100 pb-2 mb-1">Participation {num}</h4>
                                    <div>
                                      <label className={labelClass}>Mela Name <span className="text-red-500">*</span></label>
                                      <input type="text" name={`melaName${num}`} required value={formData[`melaName${num}`] || ''} onChange={handleChange} className={inputClass} placeholder="Enter Mela Name" />
                                    </div>
                                    <div>
                                      <label className={labelClass}>Tentative Participation Date <span className="text-red-500">*</span></label>
                                      <input type="month" name={`melaDate${num}`} required value={formData[`melaDate${num}`] || ''} onChange={handleChange} className={inputClass} />
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* STEP 4: Documents */}
                  {currentStep === 3 && (
                    <div className="space-y-6 animate-fadeIn">
                      
                      <div className="p-6 border border-gray-200 rounded-xl bg-blue-50 shadow-inner">
                        <label className="block text-sm font-bold text-gray-800 mb-3">Do you have an Artisan Card? <span className="text-red-500">*</span></label>
                        <div className="flex space-x-6 mb-2">
                          <label className="flex items-center space-x-2 cursor-pointer group">
                            <input type="radio" name="hasArtisanCard" value="Yes" onChange={handleChange} checked={formData.hasArtisanCard === 'Yes'} required className="text-blue-600 focus:ring-blue-500 w-4 h-4" />
                            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">Yes</span>
                          </label>
                          <label className="flex items-center space-x-2 cursor-pointer group">
                            <input type="radio" name="hasArtisanCard" value="No" onChange={handleChange} checked={formData.hasArtisanCard === 'No'} required className="text-blue-600 focus:ring-blue-500 w-4 h-4" />
                            <span className="text-sm font-medium text-gray-700 group-hover:text-blue-600 transition-colors">No</span>
                          </label>
                        </div>

                        {formData.hasArtisanCard === 'Yes' && (
                          <div className="mt-4 pt-4 border-t border-blue-200 animate-fadeIn grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <label className={labelClass}>Artisan ID <span className="text-red-500">*</span></label>
                              <input type="text" name="artisanId" required value={formData.artisanId} onChange={handleChange} className={`${inputClass} bg-white`} placeholder="e.g. ART-10293" />
                            </div>
                            <div>
                              <label className="block text-sm font-bold text-gray-800 mb-1">Upload Artisan Card <span className="text-red-500">*</span></label>
                              <p className="text-xs text-gray-500 font-medium mb-2">Issued by Govt. authority (PDF/JPG/PNG)</p>
                              <input type="file" accept=".pdf, image/jpeg, image/png" name="artisanCard" required onChange={handleChange} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-600 file:text-white hover:file:bg-blue-700 transition-colors cursor-pointer" />
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[
                          { label: 'SC Certificate', name: 'scCertificate', req: true, desc: 'Valid caste certificate (PDF/JPG/PNG)' },
                          { label: 'Income Certificate', name: 'incomeCertificate', req: true, desc: 'Recent family income proof (PDF/JPG/PNG)' },
                          { label: 'Aadhaar Document', name: 'aadhaarVoterId', req: true, desc: 'Identity verification (PDF/JPG/PNG)' },
                        ].map((doc) => (
                          <div key={doc.name} className="p-5 border border-gray-200 rounded-xl bg-white shadow-sm hover:border-blue-300 transition-colors">
                            <label className="block text-sm font-bold text-gray-800 mb-1">{doc.label} <span className="text-red-500">*</span></label>
                            <p className="text-xs text-gray-500 font-medium mb-3">{doc.desc}</p>
                            <input type="file" accept=".pdf, image/jpeg, image/png" name={doc.name} required={doc.req} onChange={handleChange} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-colors cursor-pointer" />
                          </div>
                        ))}
                      </div>

                      {formData.nsfdcLoanAvailed === 'Yes' && (
                        <div className="p-5 border border-gray-200 rounded-xl bg-gray-50 shadow-inner">
                          <label className="block text-sm font-bold text-gray-800 mb-1">Proof of Loan/Assistance <span className="text-red-500">*</span></label>
                          <p className="text-xs text-gray-500 font-medium mb-3">Required because you have availed an NSFDC loan (PDF/JPG/PNG).</p>
                          <input type="file" accept=".pdf, image/jpeg, image/png" name="proofOfLoan" required onChange={handleChange} className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-gray-200 file:text-gray-700 hover:file:bg-gray-300 transition-colors cursor-pointer" />
                        </div>
                      )}
                    </div>
                  )}

                  {/* STEP 5: Code of Conduct */}
                  {currentStep === 4 && (
                    <div className="space-y-6 animate-fadeIn">
                      <div className="bg-[#f8fafc] border border-blue-100 p-8 rounded-2xl shadow-sm">
                        <h3 className="text-lg font-bold text-gray-800 mb-4">Terms and Code of Conduct</h3>
                        <div className="bg-white p-5 rounded-xl border border-gray-200 h-48 overflow-y-auto text-sm text-gray-600 mb-6 space-y-3 shadow-inner">
                          <p><span className="font-bold text-blue-600">1.</span> The artisan agrees to strictly exhibit and sell only their own manufactured products.</p>
                          <p><span className="font-bold text-blue-600">2.</span> Discipline and decorum must be maintained during the entire duration of the mela/exhibition.</p>
                          <p><span className="font-bold text-blue-600">3.</span> The space allotted cannot be transferred or shared with any other entity without prior approval.</p>
                        </div>
                        <label className="flex items-start space-x-3 cursor-pointer group p-2 rounded-lg hover:bg-white transition-colors">
                          <input type="checkbox" name="agreedToConduct" required checked={formData.agreedToConduct} onChange={handleChange} className="mt-1 w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 transition-colors" />
                          <span className="text-gray-800 font-semibold leading-relaxed group-hover:text-blue-700 transition-colors">
                            I hereby declare that the information provided is true, and I agree to the Code of Conduct mentioned above.
                          </span>
                        </label>
                      </div>
                    </div>
                  )}
                </div>

                <div className="px-6 py-5 bg-gray-50 border-t border-gray-100 flex items-center justify-between rounded-br-2xl">
                  <button type="button" onClick={prevStep} disabled={currentStep === 0 || isSubmitting} className={`px-6 py-2.5 text-sm font-semibold rounded-lg ${currentStep === 0 ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'}`}>Back</button>
                  {currentStep < steps.length - 1 ? (
                    <button type="button" onClick={nextStep} disabled={isSubmitting} className="px-8 py-2.5 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 shadow-md">Save & Next</button>
                  ) : (
                    /* 2. UPDATED SUBMIT BUTTON TO CLEANLY HANDLE PROCESSING STATES WITH AN ANIMATED SVG */
                    <button 
                      type="submit" 
                      disabled={isSubmitting} 
                      className={`px-8 py-2.5 text-sm font-semibold text-white rounded-lg shadow-md transition-all ${
                        isSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                      }`}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                          </svg>
                          Submitting...
                        </span>
                      ) : 'Submit Application'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ======================================================== */}
        {/* TAB 2: TRACK APPLICATION */}
        {/* ======================================================== */}
        {activeTab === 'track' && (
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden animate-fadeIn">
            <div className="p-8 md:p-12">
              <div className="text-center mb-10">
                <div className="w-16 h-16 bg-[#fff4e6] rounded-full flex items-center justify-center mx-auto mb-4 border border-[#ffecd1]">
                  <svg className="w-8 h-8 text-[#ff9933]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path></svg>
                </div>
                <h2 className="text-2xl md:text-3xl font-bold text-[#1a1b41]">Track Your Registration Form</h2>
                <p className="text-gray-500 mt-2 text-sm md:text-base">Enter your Mobile Number OR Registration ID to view your submission status.</p>
              </div>

              <form onSubmit={handleTrackSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Registration ID</label>
                    <input type="text" className={inputClass} placeholder="e.g. NSFDC/MELA/..." value={trackAppId} onChange={(e) => setTrackAppId(e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Mobile Number</label>
                    <input type="tel" className={inputClass} placeholder="10-digit number" maxLength="10" value={trackMobile} onChange={(e) => setTrackMobile(e.target.value)} />
                  </div>
                </div>

                <div className="pt-4">
                  <button type="submit" disabled={isTracking || (!trackAppId && !trackMobile)} className="w-full md:w-auto px-10 py-3.5 bg-[#0D1B5E] text-white font-bold rounded-lg shadow-md hover:bg-[#1a2b85] disabled:bg-gray-400 transition-all active:scale-95 flex items-center justify-center mx-auto">
                    {isTracking ? 'Searching Database...' : 'Track Application'}
                  </button>
                </div>
              </form>

              {trackError && (
                <div className="mt-8 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm font-medium text-center animate-fadeIn">
                  <svg className="w-5 h-5 inline-block me-2 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path></svg>
                  {trackError}
                </div>
              )}

              {/* TRACKING RESULTS LIST */}
              {trackingData && trackingData.length > 0 && (
                <div className="mt-10 space-y-6 animate-fadeIn">
                  <h4 className="text-lg font-bold text-gray-800 border-b border-gray-200 pb-2">
                    Found {trackingData.length} Application(s)
                  </h4>
                  
                  {trackingData.map((app, index) => (
                    <div key={index} className="bg-gray-50 border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                      <div className="bg-white px-6 py-4 border-b border-gray-200 flex flex-wrap items-center justify-between gap-4">
                        <div>
                          <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mb-1">Registration ID</p>
                          <p className="text-lg font-black text-blue-700">{app.applicationId}</p>
                        </div>
                        <div className="text-right">
                          <span className="inline-flex items-center px-4 py-1.5 rounded-full text-xs font-bold bg-green-100 text-green-800 border border-green-200 shadow-sm">
                            <span className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse"></span>
                            {app.status || app.currentStatus || 'Submitted'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="px-6 py-5 grid grid-cols-1 sm:grid-cols-2 gap-y-4 gap-x-6">
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-0.5">Applicant Name</p>
                          <p className="text-sm font-semibold text-gray-900">{app.name || app.applicantName || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-0.5">Location</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {[app.district, app.state].filter(Boolean).join(', ') || 'N/A'}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-0.5">Product Category</p>
                          <p className="text-sm font-semibold text-gray-900">{app.productDetails || app.productCategory || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-0.5">Product</p>
                          <p className="text-sm font-semibold text-gray-900">{app.productDescription || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 font-medium mb-0.5">Submitted On</p>
                          <p className="text-sm font-semibold text-gray-900">
                            {app.submissionDate ? new Date(app.submissionDate).toLocaleDateString('en-GB') : 'N/A'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bg-[#1a1b41] text-white py-6 border-t-4 border-[#ff9933] mt-auto">
        <div className="max-w-7xl mx-auto px-4 text-center text-xs font-medium tracking-wide opacity-80 space-y-2">
          <p>© {new Date().getFullYear()} Ministry of Social Justice & Empowerment, Government of India. All rights reserved.</p>
          <p>Designed and Developed by NSFDC IT Department</p>
        </div>
      </div>
    </div>
  );
};

export default TulipNominationForm;